<script>
  import HitApi from '@/services/Api/HitApi'
  import Uploader from '@/components/Uploader'
  import { HIT_TYPES } from '@/config/constants'
  import { mapState } from 'vuex'
  import { validationMixin } from 'vuelidate'
  import { required, maxLength } from 'vuelidate/lib/validators'
  import { codeUrl, socialVideoUrl, matchRegexp } from '@/validators'

  export default {
    mixins: [validationMixin],

    components: {
      Uploader
    },

    props: {
      type: {
        type: String,
        required: true
      }
    },

    data() {
      return {
        title: '',
        description: '',
        path: '',
        is_public: true,
        tags: [],
        tags_list: [],
        loading: false,
        loading_img: false,
        loading_error: null,
        HIT_TYPES
      }
    },

    created() {
      this.axios.get('/tags')
        .then(response => (this.tags_list = response.data))

      if (this.hit) {
        this.title = this.hit.title
        this.description = this.hit.description
        this.path = this.hit.path
        this.is_public = this.hit.is_public
        this.tags = this.hit.tags
      }
    },

    destroyed() {
      this.$store.commit('Hits/setEdited', null)
    },

    computed: {
      lower_type() {
        return this.type.toLowerCase()
      },

      ...mapState({
        hit: state => state.Hits.edited_hit,
        challenge: state => state.ongoing_challenge
      })
    },

    validations() {
      var base_validation = {
        title: {
          required,
          maxLength: maxLength(70)
        },
        description: {
          required,
          maxLength: maxLength(500)
        },
        path: {
          required,
          maxLength: maxLength(190)
        }
      }

      if (this.type === HIT_TYPES.VIDEO) {
        base_validation.path.socialVideoUrl = socialVideoUrl
      } else if (this.type === HIT_TYPES.CODE) {
        base_validation.path.codeUrl = codeUrl
      } else if (this.type === HIT_TYPES.LINK) {
        base_validation.path.matchRegexp = matchRegexp('https?:\\/\\/.+', 'i')
      }

      return base_validation
    },

    methods: {
      save() {
        this.$v.$touch()

        if (this.$v.$error) {
          return
        }

        this.loading = true

        var data = {
          title: this.title,
          description: this.description,
          path: this.path,
          type: this.type,
          is_public: this.is_public,
          tags: this.tags.map(tag => tag.id)
        }

        if (
          this.$route.name === 'challenge' &&
          this.challenge &&
          [this.challenge.id + '', this.challenge.slug].indexOf(this.$route.params.id) > -1
        ) {
          data.challenge_id = this.challenge.id
        }

        var query = this.hit ? HitApi.updateHit(this.hit.id, data) : HitApi.storeHit(data)

        query.then(hit => {
          if (this.hit) {
            Object.assign(this.hit, hit)
          } else {
            hit.player.school = this.user.player.school

            this.$store.commit('Hits/addNew', hit)
            this.user.player.hits_count++
          }

          this.$store.commit('Modals/hide', 'hit_' + this.lower_type)

          return this.$store.dispatch('Alerts/add', {
            trans: 'messages.hit_added',
            type: 'success'
          })
        })
        .finally(() => (this.loading = false))
      },

      uploadStart() {
        this.loading_img = true
        this.loading_error = null
      },
      uploadError(error) {
        this.loading_img = false

        if (error.response.status === 422) {
          this.loading_error = error.response.data.file[0]
        } else {
          this.loading_error = 'errors.' + error.response.data.error
        }
      },
      uploadDone(response) {
        this.loading_img = false
        this.loading_error = null
        this.path = response.data.path
      }
    }
  }
</script>

<template>
  <form class="hit-form modal-card" @submit.prevent="save">
    <header class="modal-card-head">
      <h2 class="modal-card-title">
        <img :src="'/static/img/icon_' + lower_type + '.svg'">
        {{ $t('hit_form.title_' + lower_type) }}
      </h2>
      <button class="delete" type="button" @click="$parent.$emit('close')"></button>
    </header>

    <div class="modal-card-body">
      <p class="hit-form_path control" v-if="type != HIT_TYPES.IMAGE">
        <input class="input" type="text" v-model="path" :placeholder="$t('hit_form.path_' + lower_type)">
      </p>
      <p class="hit-form_upload" v-if="type == HIT_TYPES.IMAGE">
        <uploader url="upload" @start="uploadStart" @done="uploadDone" @error="uploadError">
          <span class="icon">
            <i class="fa fa-upload"></i>
          </span>
          <span v-html="$t('buttons.click_to_upload')" v-once></span>
        </uploader>
      </p>
      <p class="help is-info" v-show="loading_img">{{ $t('loading') }}</p>
      <p class="help is-danger" v-show="loading_error">
        {{ $t(loading_error, {max: loading_error == 'validation.max.file' ? '2Mo': ''}) }}
      </p>
      <p class="help is-danger" v-show="$v.path.$error && !$v.path.required" v-html="$t('validation.required')"></p>
      <p class="help is-danger" v-show="$v.path.$error && !$v.path.maxLength" v-html="$t('validation.max.string', {max: 190})"></p>
      <p class="help is-danger" v-show="type == HIT_TYPES.VIDEO && $v.path.$error && !$v.path.socialVideoUrl" v-html="$t('validation.video_url')"></p>
      <p class="help is-danger" v-show="type == HIT_TYPES.CODE && $v.path.$error && !$v.path.codeUrl" v-html="$t('validation.code_url')"></p>
      <p class="help is-danger" v-show="type == HIT_TYPES.LINK && $v.path.$error && !$v.path.matchRegexp" v-html="$t('validation.active_url')"></p>
      
      <div class="hit-form_fields">
        <p class="control">
          <input class="input is-large" type="text" v-model="title" :placeholder="$t('hit_form.field_title')">
        </p>
        <p class="help is-danger" v-show="$v.title.$error && !$v.title.required" v-html="$t('validation.required')"></p>
        <p class="help is-danger" v-show="$v.title.$error && !$v.title.maxLength" v-html="$t('validation.max.string', {max: 70})"></p>

        <p class="control">
          <textarea class="textarea" v-model="description" :placeholder="$t('hit_form.field_description')"></textarea>
        </p>
        <p class="help is-danger" v-show="$v.description.$error && !$v.description.required" v-html="$t('validation.required')"></p>
        <p class="help is-danger" v-show="$v.description.$error && !$v.description.maxLength" v-html="$t('validation.max.string', {max: 500})"></p>

        <p class="control">
          <multiselect
            v-model="tags"
            :max="5"
            :options="tags_list"
            :taggable="true"
            :multiple="true"
            track-by="id"
            label="name"
            :tag-placeholder="$t('hit_form.no_tag_found')"
            :placeholder="$t('hit_form.field_tags')"
            open-direction="top">
            <div slot="maxElements" v-html="$t('validation.max.array', {max: 5})"></div>
          </multiselect>
        </p>
      </div>
      
      <figure class="hit-form_preview" v-if="type == HIT_TYPES.IMAGE && path">
        <img :src="path" :alt="title">
      </figure>
    </div>

    <footer class="modal-card-foot">
      <b-dropdown v-model="is_public">
        <button class="button is-flat" type="button" slot="trigger">
          <template v-if="is_public">
            <b-icon icon="web"></b-icon>
            <span>{{ $t('hit_form.public_title') }}</span>
          </template>
          <template v-else>
            <b-icon icon="account-multiple-outline"></b-icon>
            <span>{{ $t('hit_form.private_title') }}</span>
          </template>
          <b-icon icon="menu-down"></b-icon>
        </button>

        <b-dropdown-item :value="true">
          <div class="media">
            <b-icon class="media-left" icon="web"></b-icon>
            <div class="media-content">
              <h3>{{ $t('hit_form.public_title') }}</h3>
              <small>{{ $t('hit_form.public_text') }}</small>
            </div>
          </div>
        </b-dropdown-item>

        <b-dropdown-item :value="false">
          <div class="media">
            <b-icon class="media-left" icon="account-multiple-outline"></b-icon>
            <div class="media-content">
              <h3>{{ $t('hit_form.private_title') }}</h3>
              <small>{{ $t('hit_form.private_text') }}</small>
            </div>
          </div>
        </b-dropdown-item>
      </b-dropdown>

      <button class="button is-primary" type="submit" :class="{'is-loading': loading}" :disabled="loading">
        {{ $t('buttons.send') }}
      </button>
    </footer>
  </form>
</template>

<style lang="scss">
  @import '~bulma/sass/utilities/all';

  .hit-form {
    .modal-card-head {
      background: #fff;

      img {
        height: 24px;
        vertical-align: middle;
      }
    }

    .modal-card-body {
      padding: 0;
    }

    .dropdown-menu {
      top: auto;
      bottom: 100%;
    }

    &_path,
    &_upload {
      height: 100px;
      background: $grey-lightest;
    }

    &_path input,
    &_upload label {
      display: flex;
      align-items: center;
      height: 100%;
      padding: 0 0.75em;
      background: transparent;
    }

    &_upload {
      .icon {
        vertical-align: initial;
        margin-right: pxToEm(10);
      }

      label {
        border: 5px dashed rgba(0,0,0,0.1);
        cursor: pointer;
      }

      img {
        display: inline-block;
        margin-right: 0.5em;
        vertical-align: middle;
      }
    }

    &_fields {
      padding: 1.5em;

      .input,
      .textarea {
        padding-left: 0;
        padding-right: 0;
        border: 0;
      }
    }

    .help {
      padding: 0.75em;
      font-size: $size-6;
    }

    &_preview {
      text-align: center;

      img {
        object-fit: contain;
        width: 100%;
        height: 360px;
      }
    }

    .modal-card-foot [type="submit"] {
      margin-left: pxToEm(20);
    }
  }
</style>



// WEBPACK FOOTER //
// ModalHitForm.vue?cf16caf6